import React, {useEffect, useState, useMemo} from 'react';
import {View, Text, TouchableOpacity, Alert} from 'react-native';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';

import {text} from '../text';
import {theme} from '../constants';
import {components} from '../components';
import type {RootStackParamList} from '../types';
import {resetCart} from '../store/slices/cartSlice';
import {
  useAppSelector,
  useAppNavigation,
  useAppDispatch,
} from '../hooks';
import {useCreateOrderMutation} from '../store/slices/apiSlice';
import {APP_CONFIG} from '../config';
import {formatCurrency} from '../utils/currency';

type Props = NativeStackScreenProps<RootStackParamList, 'Checkout'>;

type OrderItemAddonPayload = {
  addon_id: string | null;
  option_id: string | null;
  quantity: number;
};

const Checkout: React.FC<Props> = ({route}): JSX.Element => {
  const dispatch = useAppDispatch();
  const {total, subtotal, delivery, discount} = route.params;
  const navigation = useAppNavigation();
  const [note, setNote] = useState('');
  const user = useAppSelector((state) => state.userSlice.user);
  const cart = useAppSelector((state) => state.cartSlice.list);
  const locationState = useAppSelector((state) => state.locationSlice);
  const [createOrder, {isLoading: isCreating}] = useCreateOrderMutation();
  const [customerName, setCustomerName] = useState(user?.user_name ?? '');
  const [customerEmail, setCustomerEmail] = useState(user?.email ?? '');
  const [customerPhone, setCustomerPhone] = useState(user?.phone_number ?? '');
  const paymentMethods = useMemo(
    () => [...APP_CONFIG.checkout.paymentMethods],
    [],
  );
  const [paymentMethod, setPaymentMethod] = useState<string>(
    paymentMethods[0] ?? 'cash',
  );
  const currencyCode = APP_CONFIG.checkout.defaultCurrency;

  const appendOrderToSession = (orderData: Record<string, unknown>) => {
    const globalScope = globalThis as {
      sessionStorage?: {
        getItem: (key: string) => string | null;
        setItem: (key: string, value: string) => void;
      };
      __LEVELDELIVERY_ORDERS__?: unknown;
    };
    const storage = globalScope.sessionStorage;

    try {
      if (storage) {
        const existingRaw = storage.getItem('leveldelivery.orders');
        const parsed = existingRaw ? JSON.parse(existingRaw) : [];
        const orders = Array.isArray(parsed) ? parsed : [];
        const nextOrders = [...orders, {...orderData}];
        storage.setItem('leveldelivery.orders', JSON.stringify(nextOrders));
        globalScope.__LEVELDELIVERY_ORDERS__ = nextOrders;
        return;
      }

      const fallbackOrders = Array.isArray(globalScope.__LEVELDELIVERY_ORDERS__)
        ? [...(globalScope.__LEVELDELIVERY_ORDERS__ as any[])]
        : [];
      fallbackOrders.push({...orderData});
      globalScope.__LEVELDELIVERY_ORDERS__ = fallbackOrders;
    } catch (error) {
      console.warn('Failed to persist order history in session storage.', error);
    }
  };

  useEffect(() => {
    setCustomerName(user?.user_name ?? '');
    setCustomerEmail(user?.email ?? '');
    setCustomerPhone(user?.phone_number ?? '');
  }, [user]);

  const orderItems = useMemo(
    () =>
      cart.map((item) => ({
        id: item.backendId ?? item.id,
        name: item.name ?? null,
        price: Number.isFinite(Number(item.price)) ? Number(item.price) : null,
        quantity: Math.max(1, item.quantity ?? 1),
        image: item.image ?? null,
        addons: (() => {
          if (!Array.isArray(item.selectedAddons)) {
            return [] as OrderItemAddonPayload[];
          }

          const addonEntries = item.selectedAddons.reduce<OrderItemAddonPayload[]>(
            (entries, addon) => {
              if (!addon || !Array.isArray(addon.options)) {
                return entries;
              }

              const addonId = addon.addonId ?? addon.name ?? null;

              addon.options.forEach((option) => {
                const normalizedQuantity = Number(option.quantity);

                if (!Number.isFinite(normalizedQuantity) || normalizedQuantity <= 0) {
                  return;
                }

                entries.push({
                  addon_id: addonId,
                  option_id: option.id ?? null,
                  quantity: Math.max(1, Math.round(normalizedQuantity)),
                });
              });

              return entries;
            },
            [],
          );

          return addonEntries.length > 0 ? addonEntries : [];
        })(),
      })),
    [cart],
  );

  const handleConfirmOrder = async () => {
    if (orderItems.length === 0) {
      Alert.alert('Checkout', 'Your cart is empty.');
      return;
    }

    const trimmedName = customerName.trim();
    const trimmedEmail = customerEmail.trim();
    const trimmedPhone = customerPhone.trim();
    const hasConfirmedLocation = Boolean(
      locationState.isAddressConfirmed &&
        locationState.coordinate &&
        typeof locationState.coordinate.latitude === 'number' &&
        typeof locationState.coordinate.longitude === 'number',
    );

    if (!trimmedName || !trimmedEmail || !trimmedPhone || !hasConfirmedLocation) {
      Alert.alert('Checkout', 'Please complete the form before proceeding.');
      return;
    }

    const confirmedCoordinate = locationState.coordinate!;

    const idempotencyKey = `order-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}`;

    const payload = {
      customer: {
        name: trimmedName,
        email: trimmedEmail,
        phone: trimmedPhone,
        address: locationState.address ?? null,
        latitude: confirmedCoordinate.latitude,
        longitude: confirmedCoordinate.longitude,
      },
      items: orderItems.map((item) => ({
        ...item,
        quantity: item.quantity,
      })),
      note: note.trim() ? note.trim() : null,
      payment_type: paymentMethod ?? null,
      currency: currencyCode,
    };

    try {
      const orderResponse = await createOrder({
        payload,
        idempotencyKey,
      }).unwrap();

      appendOrderToSession({
        response: orderResponse ?? null,
        payload,
        totals: {
          total,
          subtotal,
          discount,
          delivery,
        },
        createdAt: new Date().toISOString(),
      });
      dispatch(resetCart());
      navigation.navigate('OrderSuccessful');
    } catch (error: any) {
      const validationErrors =
        error?.data?.errors && typeof error.data.errors === 'object'
          ? Object.values(error.data.errors)
              .flat()
              .map((item) => `${item}`)
              .join('\n')
          : null;
      const message =
        error?.data?.message ??
        validationErrors ??
        error?.error ??
        'Unable to create your order. Please try again.';
      Alert.alert('Checkout', message);
    }
  };

  const renderStatusBar = () => {
    return <components.StatusBar />;
  };

  const renderHeader = () => {
    return <components.Header goBack={true} />;
  };

  const renderOrderSummary = () => {
    return (
      <View
        style={{
          padding: 20,
          borderWidth: 1,
          borderRadius: 10,
          borderColor: theme.colors.accentPrimary,
          marginBottom: 30,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottomWidth: 1,
            borderBottomColor: '#DBE9F5',
            paddingBottom: 20,
            marginBottom: 20,
          }}
        >
          <text.H4>My order</text.H4>
          <text.H4>{formatCurrency(total)}</text.H4>
        </View>
        {cart.map((item, index) => {
          const key = item.cartItemId ?? `${item.id}-${index}`;
          return (
            <View key={key} style={{marginBottom: 10}}>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <text.T14>{item.name}</text.T14>
                <text.T14>
                  {item.quantity} x {formatCurrency(item.price ?? 0)}
                </text.T14>
              </View>
              {Array.isArray(item.selectedAddons) &&
                item.selectedAddons.length > 0 && (
                  <View style={{marginTop: 6}}>
                    {item.selectedAddons.map((addon) => {
                      const addonKey = `${key}-${addon.addonId}`;
                      return (
                        <View key={addonKey} style={{marginBottom: 4}}>
                          <Text
                            style={{
                              ...theme.fonts.DMSans_500Medium,
                              fontSize: 12,
                              color: theme.colors.textPrimary,
                            }}
                          >
                            {addon.name}
                          </Text>
                          {addon.options.map((option) => {
                            const optionKey = `${addonKey}-${option.id ?? option.name}`;
                            return (
                              <Text
                                key={optionKey}
                                style={{
                                  ...theme.fonts.DMSans_400Regular,
                                  fontSize: 12,
                                  color: theme.colors.textMuted,
                                }}
                              >
                                {`${option.quantity} x ${option.name}`}
                              </Text>
                            );
                          })}
                        </View>
                      );
                    })}
                  </View>
                )}
            </View>
          );
        })}
        {discount > 0 && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <text.T14>Discount</text.T14>
            <text.T14>- {formatCurrency(discount)}</text.T14>
          </View>
        )}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <text.T14>Delivery</text.T14>
          <text.T14>{formatCurrency(delivery)}</text.T14>
        </View>
      </View>
    );
  };

  const renderCustomerDetails = () => {
    return (
      <View
        style={{
          padding: 20,
          backgroundColor: theme.colors.backgroundPrimary,
          borderRadius: 10,
          marginBottom: 14,
        }}
      >
        <Text
          style={{
            ...theme.fonts.DMSans_500Medium,
            fontSize: 14,
            color: theme.colors.textPrimary,
            marginBottom: 16,
          }}
        >
          Contact information
        </Text>
        <components.InputField
          type='username'
          value={customerName}
          onChangeText={setCustomerName}
          placeholder='Full name'
          containerStyle={{marginBottom: 12}}
        />
        <components.InputField
          type='email'
          value={customerEmail}
          onChangeText={setCustomerEmail}
          placeholder='Email address'
          keyboardType='email-address'
          containerStyle={{marginBottom: 12}}
        />
        <components.InputField
          type='phone'
          value={customerPhone}
          onChangeText={setCustomerPhone}
          placeholder='Phone number'
          keyboardType='phone-pad'
        />
      </View>
    );
  };

  const renderPaymentMethod = () => {
    return (
      <View
        style={{
          padding: 20,
          backgroundColor: theme.colors.backgroundPrimary,
          borderRadius: 10,
          marginBottom: 14,
        }}
      >
        <Text
          style={{
            ...theme.fonts.DMSans_500Medium,
            fontSize: 14,
            color: theme.colors.textPrimary,
            marginBottom: 16,
          }}
        >
          Payment method
        </Text>
        {paymentMethods.length === 0 ? (
          <Text
            style={{
              ...theme.fonts.DMSans_400Regular,
              fontSize: 13,
              color: theme.colors.textMuted,
            }}
          >
            No payment methods are currently available.
          </Text>
        ) : (
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              marginHorizontal: -6,
            }}
          >
            {paymentMethods.map((method) => {
              const isSelected = paymentMethod === method;
              return (
                <TouchableOpacity
                  key={method}
                  onPress={() => setPaymentMethod(method)}
                  style={{
                    paddingHorizontal: 6,
                    paddingVertical: 4,
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      paddingVertical: 10,
                      paddingHorizontal: 14,
                      borderRadius: 20,
                      borderWidth: 1,
                      borderColor: isSelected
                        ? theme.colors.accentPrimary
                        : '#DBE9F5',
                      backgroundColor: isSelected
                        ? 'rgba(7, 193, 160, 0.1)'
                        : theme.colors.backgroundPrimary,
                    }}
                  >
                    <View
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: 7,
                        borderWidth: 1,
                        borderColor: theme.colors.accentPrimary,
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: 8,
                        backgroundColor: isSelected
                          ? theme.colors.accentPrimary
                          : theme.colors.backgroundPrimary,
                      }}
                    >
                      {isSelected && (
                        <View
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: theme.colors.backgroundPrimary,
                          }}
                        />
                      )}
                    </View>
                    <Text
                      style={{
                        ...theme.fonts.DMSans_500Medium,
                        fontSize: 13,
                        color: theme.colors.textPrimary,
                        textTransform: 'capitalize',
                      }}
                    >
                      {method}
                    </Text>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        )}
      </View>
    );
  };

  const renderInputField = () => {
    return (
      <View
        style={{
          backgroundColor: theme.colors.backgroundPrimary,
          paddingHorizontal: 20,
          paddingVertical: 20,
          borderRadius: 10,
        }}
      >
        <components.InputFieldBig
          containerStyle={{marginBottom: 14}}
          value={note}
          onChangeText={(value) => setNote(value)}
          placeholder='Add delivery note'
        />
        <components.Button
          loading={isCreating}
          title='Confirm order'
          onPress={handleConfirmOrder}
        />
      </View>
    );
  };

  const renderContent = () => {
    const contentContainerStyle = {
      flexGrow: 1,
      paddingTop: 10,
      paddingBottom: 20,
      paddingHorizontal: 20,
    };

    return (
      <components.KAScrollView
        contentContainerStyle={{...contentContainerStyle}}
      >
        {renderOrderSummary()}
        {renderCustomerDetails()}
        {renderPaymentMethod()}
        {renderInputField()}
      </components.KAScrollView>
    );
  };

  const renderHomeIndicator = () => {
    return <components.HomeIndicator />;
  };

  return (
    <components.SmartView>
      {renderStatusBar()}
      {renderHeader()}
      {renderContent()}
      {renderHomeIndicator()}
    </components.SmartView>
  );
};

export default Checkout;
